import { useState } from "react";
import { X, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  MODES, LEMBAGA_CATEGORIES, STUDENT_STATUS, STUDENT_STATUS_LABELS,
  CLIENT_STATUS, CLIENT_STATUS_LABELS,
} from "@/lib/prospectMeta";

const inputCls = "w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm focus:border-[#0f2847] focus:ring-2 focus:ring-[#0f2847]/20 focus:outline-none";
const today = () => new Date().toISOString().slice(0, 10);

function Field({ label, children }) {
  return <div><label className="text-xs font-semibold text-slate-500 mb-1 block">{label}</label>{children}</div>;
}

// mode: "convert" | "edit"; type: "individu" | "lembaga"
export function ConvertModal({ mode, type, prospect, entity, members = [], onClose, onSaved }) {
  const isL = type === "lembaga";
  const src = mode === "convert" ? prospect : entity;
  const init = mode === "convert"
    ? {
        full_name: prospect.full_name || "", guardian_name: prospect.guardian_name || "",
        institution_name: prospect.institution_name || "", institution_type: prospect.category || "",
        contact_person: prospect.contact_person || "", position: prospect.position || "",
        phone: prospect.phone_normalized || prospect.phone_raw || "", email: prospect.email || "",
        school_or_company: prospect.school_or_company || "", class_or_job: prospect.class_or_job || "",
        address: prospect.address || "", program: prospect.program_interest || "",
        learning_goal: prospect.learning_goal || "", mode: prospect.mode || "",
        desired_schedule: prospect.desired_schedule || "", participant_count: prospect.participant_count || "",
        implementation_plan: prospect.implementation_plan || "",
        assigned_to: prospect.assigned_to || "", join_date: today(),
        status: isL ? "persiapan" : "calon_terdaftar",
        notes: isL ? (prospect.negotiation_notes || "") : (prospect.notes || ""),
      }
    : {
        full_name: entity.full_name || "", guardian_name: entity.guardian_name || "",
        institution_name: entity.institution_name || "", institution_type: entity.institution_type || "",
        contact_person: entity.contact_person || "", position: entity.position || "",
        phone: entity.phone_normalized || entity.phone_raw || "", email: entity.email || "",
        school_or_company: entity.school_or_company || "", class_or_job: entity.class_or_job || "",
        address: entity.address || "", program: entity.program || "",
        learning_goal: entity.learning_goal || "", mode: entity.mode || "",
        desired_schedule: entity.desired_schedule || "", participant_count: entity.participant_count || "",
        implementation_plan: entity.implementation_plan || "",
        assigned_to: entity.assigned_to || "", join_date: entity.join_date || today(),
        status: entity.status || (isL ? "persiapan" : "calon_terdaftar"),
        notes: entity.notes || "",
      };
  const [form, setForm] = useState(init);
  const [saving, setSaving] = useState(false);
  const [dup, setDup] = useState(null);
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const statuses = isL ? CLIENT_STATUS : STUDENT_STATUS;
  const statusLabels = isL ? CLIENT_STATUS_LABELS : STUDENT_STATUS_LABELS;

  const send = async (force) => {
    setSaving(true);
    try {
      if (mode === "convert") {
        const { data } = await api.post(`/prospects/${prospect.id}/convert`, form, { params: force ? { force: true } : {} });
        toast.success("Prospek berhasil dikonversi.");
        onSaved && onSaved(data);
      } else {
        const path = isL ? `/clients/${entity.id}` : `/students/${entity.id}`;
        const { data } = await api.put(path, form, { params: force ? { force: true } : {} });
        toast.success("Data diperbarui.");
        onSaved && onSaved(data);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail?.duplicates) { setDup(detail.duplicates); }
      else { toast.error(formatApiErrorDetail(detail) || "Gagal menyimpan."); }
    } finally { setSaving(false); }
  };

  const submit = (e) => { e.preventDefault(); setDup(null); send(false); };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" data-testid="convert-modal">
      <div className="bg-white rounded-xl w-full max-w-2xl my-8 shadow-xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h2 className="font-semibold text-slate-900">
            {mode === "convert" ? (isL ? "Konversi ke Klien Lembaga" : "Konversi ke Siswa") : (isL ? "Edit Data Klien" : "Edit Data Siswa")}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-slate-100 text-slate-500" data-testid="convert-close"><X className="h-4 w-4" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3" data-testid="convert-form">
          {dup && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800" data-testid="convert-dup-warning">
              <p className="flex items-center gap-1.5 font-medium"><AlertTriangle className="h-4 w-4" /> Nomor WhatsApp / email sudah dipakai data lain.</p>
              <ul className="list-disc pl-5 mt-1 text-xs">
                {dup.map((x) => <li key={x.id}>{x.institution_name || x.full_name} — {x.phone_normalized || x.email}</li>)}
              </ul>
              <button type="button" onClick={() => send(true)} className="mt-2 px-3 py-1.5 rounded-lg bg-amber-600 text-white text-xs font-medium" data-testid="convert-force">Simpan Tetap</button>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {isL ? (
              <>
                <Field label="Nama Lembaga/Perusahaan"><input className={inputCls} value={form.institution_name} onChange={set("institution_name")} data-testid="cv-institution" required /></Field>
                <Field label="Jenis Lembaga"><select className={inputCls} value={form.institution_type} onChange={set("institution_type")} data-testid="cv-institution-type"><option value="">-</option>{LEMBAGA_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}</select></Field>
                <Field label="Nama Kontak/PIC"><input className={inputCls} value={form.contact_person} onChange={set("contact_person")} data-testid="cv-contact" /></Field>
                <Field label="Jabatan"><input className={inputCls} value={form.position} onChange={set("position")} data-testid="cv-position" /></Field>
              </>
            ) : (
              <>
                <Field label="Nama Lengkap"><input className={inputCls} value={form.full_name} onChange={set("full_name")} data-testid="cv-full-name" required /></Field>
                <Field label="Nama Orang Tua/Wali"><input className={inputCls} value={form.guardian_name} onChange={set("guardian_name")} data-testid="cv-guardian" /></Field>
                <Field label="Sekolah/Univ/Perusahaan"><input className={inputCls} value={form.school_or_company} onChange={set("school_or_company")} data-testid="cv-school" /></Field>
                <Field label="Kelas/Pekerjaan"><input className={inputCls} value={form.class_or_job} onChange={set("class_or_job")} data-testid="cv-class" /></Field>
              </>
            )}
            <Field label="Nomor WhatsApp"><input className={inputCls} value={form.phone} onChange={set("phone")} data-testid="cv-phone" /></Field>
            <Field label="Email"><input className={inputCls} value={form.email} onChange={set("email")} data-testid="cv-email" /></Field>
            <Field label="Alamat"><input className={inputCls} value={form.address} onChange={set("address")} data-testid="cv-address" /></Field>
            <Field label="Program"><input className={inputCls} value={form.program} onChange={set("program")} data-testid="cv-program" /></Field>
            <Field label="Metode"><select className={inputCls} value={form.mode} onChange={set("mode")} data-testid="cv-mode"><option value="">-</option>{MODES.map((m) => <option key={m} value={m}>{m}</option>)}</select></Field>
            {isL ? (
              <>
                <Field label="Jumlah Calon Peserta"><input className={inputCls} value={form.participant_count} onChange={set("participant_count")} data-testid="cv-participants" /></Field>
                <Field label="Rencana Pelaksanaan"><input className={inputCls} value={form.implementation_plan} onChange={set("implementation_plan")} data-testid="cv-plan" /></Field>
              </>
            ) : (
              <>
                <Field label="Tujuan Belajar"><input className={inputCls} value={form.learning_goal} onChange={set("learning_goal")} data-testid="cv-goal" /></Field>
                <Field label="Jadwal Diinginkan"><input className={inputCls} value={form.desired_schedule} onChange={set("desired_schedule")} data-testid="cv-schedule" /></Field>
              </>
            )}
            <Field label="PIC IEC"><select className={inputCls} value={form.assigned_to} onChange={set("assigned_to")} data-testid="cv-pic"><option value="">-</option>{members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}</select></Field>
            <Field label="Tanggal Bergabung"><input type="date" className={inputCls} value={form.join_date} onChange={set("join_date")} data-testid="cv-join-date" /></Field>
            <Field label="Status"><select className={inputCls} value={form.status} onChange={set("status")} data-testid="cv-status">{statuses.map((s) => <option key={s} value={s}>{statusLabels[s]}</option>)}</select></Field>
          </div>
          <Field label="Catatan"><textarea className={inputCls} rows={2} value={form.notes} onChange={set("notes")} data-testid="cv-notes" /></Field>
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50" data-testid="convert-cancel">Batal</button>
            <button type="submit" disabled={saving} className="inline-flex items-center gap-2 bg-[#0f2847] hover:bg-[#17416f] text-white text-sm font-medium px-4 py-2 rounded-lg" data-testid="convert-submit">{saving && <Loader2 className="h-4 w-4 animate-spin" />} {mode === "convert" ? "Konversi & Simpan" : "Simpan Perubahan"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
